import { JobPreferenceLinks } from './panels/JobPreferenceLinks';
import { useState, useEffect, useCallback } from 'react';
import { usePathname, useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import type {
  AccountDeletionReason,
  AccountDeletionRequestPlatform,
  NotificationPreferences,
  NotificationPreferencesPatch,
  WorkingMemoryV1,
} from '@tabbio/types';
import { colors } from '@tabbio/tokens';
import {
  AtSign,
  KeyRound,
  Mail,
  Megaphone,
  CreditCard,
  ArrowUpCircle,
  RotateCcw,
  Globe,
  Bell,
  BellOff,
  BriefcaseBusiness,
  HeartHandshake,
  HelpCircle,
  FileText,
  Lock,
  LogOut,
  ChevronRight,
  SunMoon,
  Pencil,
  Trash2,
  Eye,
  Save,
  Loader2,
  Copy,
  Check,
  CheckCircle,
  Circle,
  ExternalLink,
  XCircle,
  X,
  Building2,
  ShieldCheck,
  UserRound,
  Sparkles,
} from '@/components/icons/HugeIcons';
import { useTheme } from '../../hooks/useTheme';
import { useAuth } from '../../hooks/useAuth';
import { useLocale, type SupportedLocale } from '../../hooks/useLocale';
import { useSubscriptionState } from '../../hooks/useSubscriptionState';
import { APP_VERSION } from '../../lib/app-version';
import { useSubscriptionDisplay } from '../../lib/subscription-display';
import { canStartCancellation, getCancellationScope } from '../../lib/cancel-retention';
import { SubscriptionPlanBadge } from './SubscriptionPlanBadge';
import { SubscriptionValue } from './SubscriptionValue';
import { useWorkspace } from '../../providers/useWorkspace';
import { useRevenueCat } from '../../hooks/useRevenueCat';
import { useUpgradeModal } from '../../providers/useUpgradeModal';
import { useDialog } from '../../providers/DialogProvider';
import { EmployerCancelPlanDialog } from './EmployerCancelPlanDialog.web';
import { SubscriptionBillingPanel } from './SubscriptionBillingPanel.web';
import { requestTourStart } from '../tour/tour-bootstrap';
import { clearPostTourDestination } from '../../lib/post-tour-destination';
import { TOUR_SURFACE_HREF } from '../../lib/tour-surface';
import { signOutFromApp } from '../../lib/app-auth';
import {
  fetchMemory,
  updateMemory,
  clearMemory,
  approvalFailureCopyKey,
  confirmApproval,
  rejectApproval,
} from '../../lib/agent-threads';
import {
  getWorkingMemorySaveErrorKey,
  parseWorkingMemoryDraft,
} from '../../lib/working-memory-editor';
import {
  createMcpToken,
  fetchMcpAccessState,
  revokeMcpToken,
  type McpAccessState,
  type McpPendingApproval,
} from '../../lib/mcp-access-api';
import { useToast } from '../../hooks/useToast';
import { confirm } from '../../lib/alert.web';
import {
  openGoogleOutdatedContentTool,
  openMarketingLink,
} from '../../lib/marketing-links';
import { useUaePassStatus } from '../../hooks/useUaePassStatus';
import { useNotificationPreferences } from '../../hooks/useNotificationPreferences';
import { requestAccountDeletion } from '../../lib/account-deletion-api';
import { MemoryList } from './MemoryList.web';
import { useSignInMethods } from '../../hooks/useSignInMethods';
import { AppleIcon, GoogleIcon } from '../auth/SocialLoginButton';

// ---------------------------------------------------------------------------
// Shared building blocks
// ---------------------------------------------------------------------------

export function SettingsRow({
  icon,
  label,
  description,
  value,
  rightElement,
  onPress,
  showChevron = false,
  isLast = false,
  destructive = false,
}: {
  icon?: React.ReactNode;
  label: string;
  description?: string;
  value?: string;
  rightElement?: React.ReactNode;
  onPress?: () => void;
  showChevron?: boolean;
  isLast?: boolean;
  destructive?: boolean;
}) {
  const { theme } = useTheme();
  const rowStyle = {
    padding: '14px 16px',
    minHeight: 50,
    cursor: onPress ? 'pointer' : 'default',
    background: 'transparent',
    border: 'none',
    borderBottom: isLast ? 'none' : `0.5px solid ${theme.borderSubtle}`,
  };

  const rowContent = (
    <>
      {icon ? (
        <span
          className="flex items-center justify-center"
          style={{ width: 24, marginInlineEnd: 12 }}
        >
          {icon}
        </span>
      ) : null}
      <span className="flex-1" style={{ minWidth: 0, paddingInlineEnd: 12 }}>
        <span
          className="truncate"
          style={{
            display: 'block',
            fontSize: 14,
            color: destructive ? '#EF4444' : theme.text,
          }}
        >
          {label}
        </span>
        {description ? (
          <span
            style={{
              display: 'block',
              marginTop: 3,
              fontSize: 12,
              lineHeight: '17px',
              color: theme.textTertiary,
            }}
          >
            {description}
          </span>
        ) : null}
      </span>
      <span className="flex items-center gap-1.5">
        {rightElement}
        {value ? (
          <span style={{ fontSize: 14, color: theme.textTertiary }}>{value}</span>
        ) : null}
        {showChevron ? (
          <ChevronRight size={16} color={theme.textTertiary} strokeWidth={1.75} />
        ) : null}
      </span>
    </>
  );

  if (!onPress) {
    return (
      <div
        className="flex items-center w-full text-start transition-colors"
        style={rowStyle}
      >
        {rowContent}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onPress}
      className="flex items-center w-full text-start transition-colors"
      style={rowStyle}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = theme.surface;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
      }}
    >
      {rowContent}
    </button>
  );
}

export function PanelHeader({
  title,
  subtitle,
}: {
  title: string;
  subtitle?: string;
}) {
  const { theme } = useTheme();
  return (
    <div style={{ marginBottom: 16 }}>
      <h2
        style={{
          fontSize: 20,
          fontWeight: 600,
          color: theme.text,
          margin: 0,
          lineHeight: 1.3,
        }}
      >
        {title}
      </h2>
      {subtitle ? (
        <p
          style={{
            fontSize: 13,
            color: theme.textTertiary,
            margin: '4px 0 0',
          }}
        >
          {subtitle}
        </p>
      ) : null}
    </div>
  );
}

const ICON_SIZE = 18;
const ICON_STROKE = 1.75;
const MAX_ACCOUNT_DELETION_REASON_LENGTH = 1000;

// ---------------------------------------------------------------------------
// Account panel
// ---------------------------------------------------------------------------

export function AccountPanel({
  onOpenManageAccount,
  onSignedOut,
  onOpenPrivacy,
  onOpenDataManagement,
}: {
  onOpenManageAccount: () => void;
  onSignedOut?: () => void;
  onOpenPrivacy: () => void;
  onOpenDataManagement: () => void;
}) {
  const { theme } = useTheme();
  const { user } = useAuth();
  const { t } = useTranslation('common');
  const router = useRouter();
  const uaePassStatusQuery = useUaePassStatus(!!user?.id);
  const subscriptionDisplay = useSubscriptionDisplay('web');

  const displayName = user?.name || t('userAccount');
  const email = user?.email || '';
  const initials = displayName
    .split(' ')
    .map((n: string) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const isUaePassIdentityLocked = Boolean(
    user?.isUaePassVerified ||
      uaePassStatusQuery.data?.isLinked ||
      uaePassStatusQuery.data?.isVerified,
  );

  const handleSignOut = async () => {
    const ok = await confirm({
      title: t('settings.signOutTitle'),
      message: t('settings.signOutMessage'),
      confirmLabel: t('actions.signOut'),
      cancelLabel: t('actions.cancel'),
      destructive: true,
    });
    if (!ok) return;
    onSignedOut?.();
    void signOutFromApp();
    router.replace('/');
  };

  const iconColor = theme.textSecondary;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <PanelHeader
        title={t('settings.categories.account')}
        subtitle={subscriptionDisplay.fullPlanLabel}
      />

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          padding: 14,
          borderRadius: 12,
          background: theme.surface,
        }}
      >
        <div
          style={{
            width: 52,
            height: 52,
            borderRadius: 26,
            background: '#5e29ff',
            color: '#fff',
            fontSize: 18,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {initials}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: theme.text }}>
            {displayName}
          </div>
          <div style={{ fontSize: 13, color: theme.textTertiary, marginTop: 2 }}>
            {email}
          </div>
        </div>
        {!isUaePassIdentityLocked ? (
          <button
            onClick={onOpenManageAccount}
            style={{
              padding: '6px 14px',
              border: `1px solid ${theme.buttonOutlineBorder}`,
              borderRadius: 999,
              background: 'transparent',
              fontSize: 12,
              fontWeight: 600,
              color: theme.text,
              cursor: 'pointer',
            }}
          >
            {t('settings.editProfile')}
          </button>
        ) : null}
      </div>

      <div style={{ borderRadius: 12, background: theme.surface, overflow: 'hidden' }}>
        <SettingsRow
          icon={<Mail size={ICON_SIZE} color={iconColor} strokeWidth={ICON_STROKE} />}
          label={t('settings.email')}
          value={email}
        />
        <SettingsRow
          icon={
            <UserRound size={ICON_SIZE} color={iconColor} strokeWidth={ICON_STROKE} />
          }
          label={t('settings.name')}
          value={displayName}
        />
        <SettingsRow
          icon={
            <ShieldCheck
              size={ICON_SIZE}
              color={iconColor}
              strokeWidth={ICON_STROKE}
            />
          }
          label={t('settings.accountDeletionFlow.accountControls')}
          description={t('settings.accountDeletionFlow.accountControlsDescription')}
          showChevron
          isLast
          onPress={onOpenManageAccount}
        />
      </div>

      <div style={{ borderRadius: 12, background: theme.surface, overflow: 'hidden' }}>
        <SettingsRow label={t('settings.privacy.title')} description={t('settings.cvPrivacy.description')} showChevron onPress={onOpenPrivacy} />
        <SettingsRow label={t('settings.dataManagement.title')} description={t('settings.dataManagement.description')} showChevron isLast onPress={onOpenDataManagement} />
      </div>

      <div style={{ borderRadius: 12, background: theme.surface, overflow: 'hidden' }}>
        <SettingsRow
          icon={<LogOut size={ICON_SIZE} color="#EF4444" strokeWidth={ICON_STROKE} />}
          label={t('actions.signOut')}
          onPress={handleSignOut}
          destructive
          isLast
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Manage account panel: Change name / username / email / password + delete.
// Mirrors the native `settings/manage-account` screen so web parity holds.
// ---------------------------------------------------------------------------

export type AccountEditSection = 'name' | 'username' | 'email' | 'password';

export function ManageAccountPanel({
  onOpenSection,
}: {
  onOpenSection: (section: AccountEditSection) => void;
}) {
  const { theme } = useTheme();
  const { t } = useTranslation('common');
  const [isDeletionDialogOpen, setIsDeletionDialogOpen] = useState(false);
  const iconColor = theme.textSecondary;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <PanelHeader
        title={t('settings.manageCredentials.title')}
        subtitle={t('settings.manageCredentials.description')}
      />

      <div style={{ borderRadius: 12, background: theme.surface, overflow: 'hidden' }}>
        <SettingsRow
          icon={
            <UserRound size={ICON_SIZE} color={iconColor} strokeWidth={ICON_STROKE} />
          }
          label={t('settings.manageCredentials.changeName')}
          description={t('settings.manageCredentials.changeNameDescription')}
          showChevron
          onPress={() => onOpenSection('name')}
        />
        <SettingsRow
          icon={<AtSign size={ICON_SIZE} color={iconColor} strokeWidth={ICON_STROKE} />}
          label={t('settings.manageCredentials.changeUsername')}
          description={t('settings.manageCredentials.changeUsernameDescription')}
          showChevron
          onPress={() => onOpenSection('username')}
        />
        <SettingsRow
          icon={<Mail size={ICON_SIZE} color={iconColor} strokeWidth={ICON_STROKE} />}
          label={t('settings.manageCredentials.changeEmail')}
          description={t('settings.manageCredentials.changeEmailDescription')}
          showChevron
          onPress={() => onOpenSection('email')}
        />
        <SettingsRow
          icon={
            <KeyRound size={ICON_SIZE} color={iconColor} strokeWidth={ICON_STROKE} />
          }
          label={t('settings.manageCredentials.changePassword')}
          description={t('settings.manageCredentials.changePasswordDescription')}
          showChevron
          onPress={() => onOpenSection('password')}
        />
        <SettingsRow
          icon={<Trash2 size={ICON_SIZE} color="#EF4444" strokeWidth={ICON_STROKE} />}
          label={t('settings.manageCredentials.deleteAccount')}
          description={t('settings.manageCredentials.deleteAccountDescription')}
          destructive
          isLast
          onPress={() => setIsDeletionDialogOpen(true)}
        />
      </div>

      <SignInMethodsRows onSetPassword={() => onOpenSection('password')} />

      <AccountDeletionDialog
        open={isDeletionDialogOpen}
        onClose={() => setIsDeletionDialogOpen(false)}
      />
    </div>
  );
}

/**
 * Web mirror of `panels/SignInMethodsSection` for the settings dialog, which
 * renders DOM rows rather than the native route's React Native ones. Both read
 * the same `useSignInMethods()` hook, so only the markup is duplicated.
 * "Set password" stays in the dialog: it opens the password view the dialog
 * already has instead of the `/account-edit` page.
 */
function SignInMethodsRows({ onSetPassword }: { onSetPassword: () => void }) {
  const { theme } = useTheme();
  const { t } = useTranslation('common');
  const {
    providers,
    isConnected,
    hasPassword,
    canDisconnect,
    isLoading,
    pendingProvider,
    connect,
    disconnect,
  } = useSignInMethods();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <span
        style={{
          fontSize: 12,
          textTransform: 'uppercase',
          letterSpacing: 0.6,
          color: theme.textTertiary,
          paddingInlineStart: 4,
        }}
      >
        {t('settings.signInMethods.title')}
      </span>

      <div
        style={{ borderRadius: 12, background: theme.surface, overflow: 'hidden' }}
      >
        <SettingsRow
          icon={
            <KeyRound size={ICON_SIZE} color={theme.textSecondary} strokeWidth={ICON_STROKE} />
          }
          label={t('settings.signInMethods.providers.password')}
          description={
            hasPassword
              ? t('settings.signInMethods.passwordSet')
              : t('settings.signInMethods.passwordNotSet')
          }
          value={hasPassword ? undefined : t('settings.signInMethods.setPassword')}
          showChevron={!hasPassword}
          onPress={hasPassword ? undefined : onSetPassword}
        />

        {providers.map((provider, index) => {
          const connected = isConnected(provider);
          const isPending = pendingProvider === provider;
          const canAct = !isLoading && !pendingProvider;
          const showDisconnect = connected && canDisconnect;

          return (
            <SettingsRow
              key={provider}
              icon={
                provider === 'google' ? (
                  <GoogleIcon size={ICON_SIZE} />
                ) : (
                  <AppleIcon size={ICON_SIZE} color={theme.text} />
                )
              }
              label={t(`settings.signInMethods.providers.${provider}`)}
              description={
                connected
                  ? t('settings.signInMethods.connected')
                  : t('settings.signInMethods.notConnected')
              }
              value={
                isPending
                  ? t('settings.signInMethods.working')
                  : connected
                    ? showDisconnect
                      ? t('settings.signInMethods.disconnect')
                      : undefined
                    : t('settings.signInMethods.connect')
              }
              isLast={index === providers.length - 1}
              onPress={
                !canAct
                  ? undefined
                  : connected
                    ? showDisconnect
                      ? () => void disconnect(provider)
                      : undefined
                    : () => void connect(provider)
              }
            />
          );
        })}
      </div>
    </div>
  );
}

type AccountDeletionDialogStep = 'reason' | 'message' | 'details';

const WEB_ACCOUNT_DELETION_REASONS: Array<{
  id: AccountDeletionReason;
  icon: React.ComponentType<{ size?: number; color?: string; strokeWidth?: number }>;
}> = [
  { id: 'too_many_notifications', icon: BellOff },
  { id: 'privacy_concern', icon: ShieldCheck },
  { id: 'not_useful', icon: HeartHandshake },
  { id: 'found_job', icon: BriefcaseBusiness },
  { id: 'cost', icon: CreditCard },
  { id: 'other', icon: HelpCircle },
];

export function AccountDeletionDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { theme } = useTheme();
  const { t } = useTranslation('common');
  const { locale } = useLocale();
  const pathname = usePathname();
  const router = useRouter();
  const toast = useToast();
  const [step, setStep] = useState<AccountDeletionDialogStep>('reason');
  const [selectedReason, setSelectedReason] =
    useState<AccountDeletionReason | null>(null);
  const [details, setDetails] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!open) return null;

  const reason = selectedReason ?? 'other';
  const detailsTooLong = details.length > MAX_ACCOUNT_DELETION_REASON_LENGTH;
  const detailsRequired = selectedReason === 'other';
  const detailsMissing = detailsRequired && details.trim().length < 3;
  const canSubmit = Boolean(selectedReason) && !detailsTooLong && !detailsMissing;

  const closeDialog = () => {
    if (isSubmitting) return;
    setStep('reason');
    setSelectedReason(null);
    setDetails('');
    onClose();
  };

  const submitDeletion = async () => {
    if (!selectedReason || !canSubmit || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const deletion = await requestAccountDeletion({
        reasonCategory: selectedReason,
        reason: details.trim() || undefined,
        route: pathname,
        platform: 'web' satisfies AccountDeletionRequestPlatform,
        appVersion: APP_VERSION,
        locale,
        metadata: {
          source: 'settings_account_delete_dialog',
          searchResultNoticeVersion: 'google_outdated_content_v1',
          viewportWidth:
            typeof window === 'undefined' ? null : Math.round(window.innerWidth),
          viewportHeight:
            typeof window === 'undefined' ? null : Math.round(window.innerHeight),
        },
      });
      // The durable receipt id (E10), absent from servers before it.
      toast.success(
        t('settings.accountDeletionFlow.deletedTitle'),
        deletion.receiptId
          ? t('settings.accountDeletionFlow.deletedMessageWithReceipt', {
              receiptId: deletion.receiptId,
            })
          : t('settings.accountDeletionFlow.deletedMessage'),
      );
      onClose();
      await signOutFromApp();
      router.replace('/');
    } catch (error) {
      toast.error(
        t('settings.accountDeletionFlow.errorTitle'),
        error instanceof Error
          ? error.message
          : t('settings.accountDeletionFlow.errorMessage'),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('settings.accountDeletionFlow.deleteAccountTitle')}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        background: 'rgba(0, 0, 0, 0.32)',
      }}
    >
      <div
        style={{
          width: 'min(520px, 100%)',
          maxHeight: 'min(720px, calc(100vh - 40px))',
          overflow: 'hidden',
          borderRadius: 20,
          background: theme.background,
          boxShadow: '0 24px 80px rgba(15, 23, 42, 0.22)',
          border: `1px solid ${theme.borderSubtle}`,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
            padding: '18px 20px 14px',
            borderBottom: `1px solid ${theme.borderSubtle}`,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: theme.text }}>
              {t('settings.accountDeletionFlow.deleteAccountTitle')}
            </div>
            <div
              style={{
                marginTop: 3,
                fontSize: 13,
                lineHeight: '18px',
                color: theme.textTertiary,
              }}
            >
              {t('settings.accountDeletionFlow.deleteAccountDescription')}
            </div>
          </div>
          <button
            type="button"
            onClick={closeDialog}
            disabled={isSubmitting}
            aria-label={t('actions.close')}
            style={{
              width: 32,
              height: 32,
              borderRadius: 16,
              border: 'none',
              background: theme.surface,
              color: theme.text,
              cursor: isSubmitting ? 'default' : 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <X size={16} strokeWidth={ICON_STROKE} />
          </button>
        </div>

        <div
          style={{
            padding: 20,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
            {[1, 2, 3].map((item) => {
              const current = step === 'reason' ? 1 : step === 'message' ? 2 : 3;
              return (
                <span
                  key={item}
                  style={{
                    width: 24,
                    height: 4,
                    borderRadius: 2,
                    background:
                      item <= current ? colors.primary : theme.surfaceTertiary,
                  }}
                />
              );
            })}
          </div>

          {step === 'reason' ? (
            <>
              <div>
                <div style={{ fontSize: 20, fontWeight: 700, color: theme.text }}>
                  {t('settings.accountDeletionFlow.reasonStepTitle')}
                </div>
                <p
                  style={{
                    margin: '5px 0 0',
                    fontSize: 13,
                    lineHeight: '19px',
                    color: theme.textTertiary,
                  }}
                >
                  {t('settings.accountDeletionFlow.reasonStepBody')}
                </p>
              </div>
              <div
                style={{
                  borderRadius: 12,
                  background: theme.surface,
                  overflow: 'hidden',
                }}
              >
                {WEB_ACCOUNT_DELETION_REASONS.map((option, index) => {
                  const Icon = option.icon;
                  const isSelected = selectedReason === option.id;
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => setSelectedReason(option.id)}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                        padding: '12px 14px',
                        border: 'none',
                        borderBottom:
                          index === WEB_ACCOUNT_DELETION_REASONS.length - 1
                            ? 'none'
                            : `1px solid ${theme.borderSubtle}`,
                        background: isSelected ? theme.surfaceTertiary : 'transparent',
                        color: theme.text,
                        textAlign: 'start',
                        cursor: 'pointer',
                      }}
                    >
                      <Icon
                        size={18}
                        color={isSelected ? colors.primary : theme.textSecondary}
                        strokeWidth={ICON_STROKE}
                      />
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span
                          style={{
                            display: 'block',
                            fontSize: 14,
                            fontWeight: 600,
                          }}
                        >
                          {t(`settings.accountDeletionFlow.reasons.${option.id}.label`)}
                        </span>
                        <span
                          style={{
                            display: 'block',
                            marginTop: 2,
                            fontSize: 12,
                            lineHeight: '17px',
                            color: theme.textTertiary,
                          }}
                        >
                          {t(
                            `settings.accountDeletionFlow.reasons.${option.id}.description`,
                          )}
                        </span>
                      </span>
                      {isSelected ? (
                        <CheckCircle size={18} color={colors.primary} />
                      ) : (
                        <Circle size={18} color={theme.textTertiary} />
                      )}
                    </button>
                  );
                })}
              </div>
              <PrimaryDialogButton
                label={t('actions.next')}
                disabled={!selectedReason}
                onClick={() => setStep('message')}
              />
            </>
          ) : null}

          {step === 'message' ? (
            <>
              <div
                style={{
                  borderRadius: 12,
                  background: theme.surface,
                  padding: 16,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                }}
              >
                <ShieldCheck size={22} color={colors.primary} strokeWidth={ICON_STROKE} />
                <div style={{ fontSize: 18, fontWeight: 700, color: theme.text }}>
                  {t(`settings.accountDeletionFlow.retention.${reason}.title`)}
                </div>
                <p
                  style={{
                    margin: 0,
                    fontSize: 13,
                    lineHeight: '19px',
                    color: theme.textTertiary,
                  }}
                >
                  {t(`settings.accountDeletionFlow.retention.${reason}.body`)}
                </p>
              </div>
              <div
                style={{
                  borderRadius: 12,
                  border: `1px solid ${colors.danger}`,
                  padding: 14,
                  display: 'flex',
                  gap: 10,
                  alignItems: 'flex-start',
                }}
              >
                <Trash2 size={18} color={colors.danger} strokeWidth={ICON_STROKE} />
                <span style={{ minWidth: 0 }}>
                  <span
                    style={{
                      display: 'block',
                      fontSize: 14,
                      fontWeight: 700,
                      color: colors.danger,
                    }}
                  >
                    {t('settings.accountDeletionFlow.finalWarningTitle')}
                  </span>
                  <span
                    style={{
                      display: 'block',
                      marginTop: 2,
                      fontSize: 12,
                      lineHeight: '17px',
                      color: theme.textTertiary,
                    }}
                  >
                    {t('settings.accountDeletionFlow.finalWarningBody')}
                  </span>
                </span>
              </div>
              <SecondaryDialogButton
                label={t('settings.accountDeletionFlow.keepAccount')}
                onClick={closeDialog}
              />
              <DangerSoftDialogButton
                label={t('settings.accountDeletionFlow.continueDeletion')}
                onClick={() => setStep('details')}
              />
              <GhostDialogButton label={t('actions.back')} onClick={() => setStep('reason')} />
            </>
          ) : null}

          {step === 'details' ? (
            <>
              <div>
                <div style={{ fontSize: 20, fontWeight: 700, color: theme.text }}>
                  {t('settings.accountDeletionFlow.finalStepTitle')}
                </div>
                <p
                  style={{
                    margin: '5px 0 0',
                    fontSize: 13,
                    lineHeight: '19px',
                    color: theme.textTertiary,
                  }}
                >
                  {t('settings.accountDeletionFlow.finalStepBody')}
                </p>
              </div>
              <label
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 6,
                  fontSize: 13,
                  fontWeight: 700,
                  color: theme.text,
                }}
              >
                {reason === 'other'
                  ? t('settings.accountDeletionFlow.otherReasonLabel')
                  : t('settings.accountDeletionFlow.detailsLabel')}
                <textarea
                  value={details}
                  maxLength={MAX_ACCOUNT_DELETION_REASON_LENGTH + 1}
                  placeholder={
                    reason === 'other'
                      ? t('settings.accountDeletionFlow.otherReasonPlaceholder')
                      : t('settings.accountDeletionFlow.detailsPlaceholder')
                  }
                  onChange={(event) => setDetails(event.currentTarget.value)}
                  style={{
                    minHeight: 108,
                    resize: 'vertical',
                    border: `1px solid ${
                      detailsTooLong || detailsMissing
                        ? colors.danger
                        : theme.borderSubtle
                    }`,
                    borderRadius: 10,
                    background: theme.surface,
                    color: theme.text,
                    padding: '10px 12px',
                    font: 'inherit',
                    fontSize: 13,
                    lineHeight: '19px',
                    outline: 'none',
                  }}
                />
              </label>
              <div
                style={{
                  fontSize: 12,
                  color:
                    detailsTooLong || detailsMissing
                      ? colors.danger
                      : theme.textTertiary,
                  textAlign: 'end',
                }}
              >
                {detailsMissing
                  ? t('settings.accountDeletionFlow.otherReasonRequired')
                  : t('settings.accountDeletionFlow.reasonCounter', {
                      count: details.length,
                      max: MAX_ACCOUNT_DELETION_REASON_LENGTH,
                    })}
              </div>
              <WebSearchResultNotice />
              <PrimaryDialogButton
                label={
                  isSubmitting
                    ? t('settings.accountDeletionFlow.deleting')
                    : t('settings.accountDeletionFlow.deleteAction')
                }
                danger
                disabled={!canSubmit || isSubmitting}
                onClick={() => {
                  void submitDeletion();
                }}
              />
              <GhostDialogButton
                label={t('actions.back')}
                disabled={isSubmitting}
                onClick={() => setStep('message')}
              />
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function WebSearchResultNotice() {
  const { theme } = useTheme();
  const { t } = useTranslation('common');

  return (
    <aside
      aria-labelledby="account-deletion-search-results-title"
      style={{
        borderRadius: 12,
        border: `1px solid ${theme.borderSubtle}`,
        background: theme.surface,
        padding: 14,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Globe size={18} color={colors.primary} strokeWidth={ICON_STROKE} />
        <div
          id="account-deletion-search-results-title"
          style={{ fontSize: 14, fontWeight: 700, color: theme.text }}
        >
          {t('settings.accountDeletionFlow.searchResultsTitle')}
        </div>
      </div>
      <p
        style={{
          margin: 0,
          fontSize: 12,
          lineHeight: '17px',
          color: theme.textTertiary,
        }}
      >
        {t('settings.accountDeletionFlow.searchResultsBody')}
      </p>
      <p
        style={{
          margin: 0,
          fontSize: 11,
          lineHeight: '16px',
          color: theme.textTertiary,
        }}
      >
        {t('settings.accountDeletionFlow.searchResultsHelper')}
      </p>
      <button
        type="button"
        onClick={() => {
          void openGoogleOutdatedContentTool();
        }}
        style={{
          minHeight: 36,
          borderRadius: 999,
          border: `1px solid ${theme.buttonOutlineBorder}`,
          background: 'transparent',
          color: theme.text,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 7,
          padding: '8px 12px',
          fontSize: 12,
          fontWeight: 700,
        }}
      >
        {t('settings.accountDeletionFlow.searchResultsAction')}
        <ExternalLink size={14} strokeWidth={ICON_STROKE} aria-hidden="true" />
      </button>
    </aside>
  );
}

function PrimaryDialogButton({
  label,
  onClick,
  disabled,
  danger,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      style={{
        width: '100%',
        border: 'none',
        borderRadius: 999,
        background: danger ? colors.danger : colors.primary,
        color: '#fff',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        fontSize: 13,
        fontWeight: 700,
        padding: '10px 14px',
      }}
    >
      {label}
    </button>
  );
}

function SecondaryDialogButton({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  const { theme } = useTheme();
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: '100%',
        border: `1px solid ${theme.buttonOutlineBorder}`,
        borderRadius: 999,
        background: 'transparent',
        color: theme.text,
        cursor: 'pointer',
        fontSize: 13,
        fontWeight: 700,
        padding: '9px 14px',
      }}
    >
      {label}
    </button>
  );
}

function DangerSoftDialogButton({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: '100%',
        border: 'none',
        borderRadius: 999,
        background: 'rgba(239, 68, 68, 0.12)',
        color: colors.danger,
        cursor: 'pointer',
        fontSize: 13,
        fontWeight: 700,
        padding: '10px 14px',
      }}
    >
      {label}
    </button>
  );
}

function GhostDialogButton({
  label,
  onClick,
  disabled,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  const { theme } = useTheme();
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      style={{
        width: '100%',
        border: 'none',
        borderRadius: 999,
        background: 'transparent',
        color: theme.textSecondary,
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        fontSize: 13,
        fontWeight: 700,
        padding: '9px 14px',
      }}
    >
      {label}
    </button>
  );
}
// ---------------------------------------------------------------------------
// Billing / subscription panel
// ---------------------------------------------------------------------------

/**
 * Individual plans get the subscription-journeys "Plan & billing" (D) with
 * the multi-step cancel journey; employer (seat) billing keeps today's panel.
 */
export function BillingPanel() {
  const { snapshot } = useSubscriptionState();
  const { mode } = useWorkspace();
  const isEmployer = snapshot ? snapshot.planSet === 'employer' : mode === 'employer';
  return isEmployer ? <EmployerBillingPanel /> : <SubscriptionBillingPanel />;
}

function EmployerBillingPanel() {
  const { theme } = useTheme();
  const { t } = useTranslation('common');
  const { catalog, snapshot, isLoading } = useSubscriptionState();
  const subscriptionDisplay = useSubscriptionDisplay('web');
  const { companyId } = useWorkspace();
  const { openManagementUrl, syncSubscription } = useRevenueCat();
  const upgradeModal = useUpgradeModal();
  const toast = useToast();
  const cancelScope = getCancellationScope(snapshot, companyId);
  const [cancelTarget, setCancelTarget] = useState<string | null>(null);
  const cancelDialogOpen = cancelTarget === cancelScope;
  useEffect(() => {
    setCancelTarget(null);
  }, [cancelScope]);
  const planLabel =
    subscriptionDisplay.isUnknownState && !isLoading
      ? t('settings.billingUnavailable')
      : subscriptionDisplay.fullPlanLabel;

  const canOpenManageSubscription = Boolean(
    snapshot?.managementUrl || subscriptionDisplay.managementFallbackUrl,
  );
  const hasManageActionableDestination =
    subscriptionDisplay.showManageAction && canOpenManageSubscription;

  const openManagementDestination = async () => {
    if (snapshot?.managementUrl) {
      await openManagementUrl(
        snapshot.planSet === 'employer' ? (companyId ?? null) : null,
      );
      return;
    }

    if (subscriptionDisplay.managementFallbackUrl) {
      window.location.assign(subscriptionDisplay.managementFallbackUrl);
      return;
    }

    throw new Error(
      subscriptionDisplay.managementDescription ||
        t('settings.billingUnavailable'),
    );
  };

  const handleManageSubscription = async () => {
    try {
      if (
        snapshot?.managementUrl ||
        subscriptionDisplay.managementFallbackUrl
      ) {
        await openManagementDestination();
        return;
      }

      if (!subscriptionDisplay.isPaid) {
        upgradeModal.open(subscriptionDisplay.upgradeMode);
        return;
      }

      throw new Error(
        subscriptionDisplay.managementDescription ||
          t('settings.billingUnavailable'),
      );
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : subscriptionDisplay.managementDescription ||
            t('settings.billingUnavailable');
      toast.error(t('settings.billingUnavailable'), message);
    }
  };

  const handleCancelContinueToStore = async () => {
    try {
      await openManagementDestination();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : subscriptionDisplay.managementDescription ||
            t('settings.billingUnavailable');
      toast.error(t('settings.billingUnavailable'), message);
      throw error;
    }
  };

  const handleSubscriptionAction = async () => {
    try {
      await syncSubscription({
        planSet: subscriptionDisplay.upgradeMode,
        companyId:
          subscriptionDisplay.upgradeMode === 'employer'
            ? (companyId ?? null)
            : null,
      });
      toast.success(t('settings.subscriptionSynced'));
    } catch (error) {
      console.error('[SettingsSections.web] subscription sync failed', error);
      toast.error(t('settings.syncFailed'), t('settings.syncFailedMessage'));
    }
  };

  const handleUpgrade = () => {
    upgradeModal.open(subscriptionDisplay.upgradeMode);
  };

  const canCancel =
    canStartCancellation(snapshot) && hasManageActionableDestination;
  const iconColor = theme.textSecondary;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 10,
        }}
      >
        {subscriptionDisplay.isPaid ? (
          <SubscriptionPlanBadge label={subscriptionDisplay.tierLabel} />
        ) : (
          <span style={{ fontSize: 20 }}>{planLabel}</span>
        )}
        <h2
          style={{ margin: 0, fontSize: 28, fontWeight: 400, lineHeight: 1.2 }}
        >
          {t('settings.subscriptionValue.title')}
        </h2>
        <p
          style={{
            margin: 0,
            color: 'var(--text-secondary)',
            fontSize: 14,
            lineHeight: 1.5,
          }}
        >
          {subscriptionDisplay.billingStatusDescription ||
            subscriptionDisplay.billingStatusLabel ||
            t('settings.subscriptionValue.subtitle')}
        </p>
      </div>
      {snapshot ? (
        <SubscriptionValue
          catalog={catalog}
          snapshot={snapshot}
          planLabel={subscriptionDisplay.tierLabel}
          showAllowances
        />
      ) : (
        <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 14 }}>
          {t(
            isLoading
              ? 'settings.checkingSubscription'
              : 'settings.subscriptionValue.usageUnavailable',
          )}
        </p>
      )}
      <div
        style={{
          borderRadius: 16,
          background: theme.surface,
          overflow: 'hidden',
        }}
      >
        <SettingsRow
          icon={
            <CreditCard
              size={ICON_SIZE}
              color={iconColor}
              strokeWidth={ICON_STROKE}
            />
          }
          label={subscriptionDisplay.subscriptionRowLabel}
          value={planLabel}
          onPress={
            hasManageActionableDestination
              ? () => {
                  void handleManageSubscription();
                }
              : snapshot && !subscriptionDisplay.isPaid
                ? handleUpgrade
                : undefined
          }
          showChevron={
            hasManageActionableDestination ||
            Boolean(snapshot && !subscriptionDisplay.isPaid)
          }
        />
        {subscriptionDisplay.showUpgradeAction &&
        subscriptionDisplay.upgradeLabel &&
        snapshot ? (
          <SettingsRow
            icon={
              <ArrowUpCircle
                size={ICON_SIZE}
                color={iconColor}
                strokeWidth={ICON_STROKE}
              />
            }
            label={subscriptionDisplay.upgradeLabel}
            onPress={handleUpgrade}
          />
        ) : null}
        <SettingsRow
          icon={
            <RotateCcw
              size={ICON_SIZE}
              color={iconColor}
              strokeWidth={ICON_STROKE}
            />
          }
          label={subscriptionDisplay.billingActionLabel}
          onPress={() => {
            void handleSubscriptionAction();
          }}
          isLast
        />
      </div>
      {subscriptionDisplay.managementDescription ? (
        <p
          style={{
            margin: 0,
            fontSize: 13,
            lineHeight: 1.5,
            color: 'var(--text-secondary)',
          }}
        >
          {subscriptionDisplay.managementDescription}
        </p>
      ) : null}
      {canCancel ? (
        <button
          type="button"
          onClick={() => setCancelTarget(cancelScope)}
          style={{
            alignSelf: 'center',
            minHeight: 44,
            padding: '12px 16px',
            background: 'transparent',
            border: 0,
            color: 'var(--text-secondary)',
            fontSize: 14,
            textDecoration: 'underline',
            cursor: 'pointer',
          }}
        >
          {t('settings.cancelFlow.cancelPlan')}
        </button>
      ) : null}
      <EmployerCancelPlanDialog
        key={`${snapshot?.planSet}:${companyId ?? ''}:${snapshot?.tier}`}
        open={cancelDialogOpen && canCancel}
        onClose={() => setCancelTarget(null)}
        onContinueToStore={handleCancelContinueToStore}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Preferences (theme + language)
// ---------------------------------------------------------------------------

const LOCALES: SupportedLocale[] = ['en', 'ar'];
const LOCALE_LABELS: Record<SupportedLocale, string> = {
  en: 'English',
  ar: 'العربية',
};

function SettingsSelectRow<T extends string>({
  icon,
  label,
  value,
  options,
  onChange,
  isLast,
}: {
  icon: React.ReactNode;
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (next: T) => void;
  isLast?: boolean;
}) {
  const { theme } = useTheme();

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 16px',
        minHeight: 50,
        borderBottom: isLast ? 'none' : `0.5px solid ${theme.borderSubtle}`,
      }}
    >
      <span
        style={{
          width: 24,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon}
      </span>
      <span style={{ flex: 1, fontSize: 14, color: theme.text }}>{label}</span>
      <div style={{ position: 'relative', display: 'inline-flex' }}>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value as T)}
          style={{
            appearance: 'none',
            WebkitAppearance: 'none',
            MozAppearance: 'none',
            border: `1px solid ${theme.borderSubtle}`,
            borderRadius: 8,
            background: theme.background,
            color: theme.text,
            fontSize: 13,
            fontWeight: 500,
            padding: '6px 28px 6px 12px',
            cursor: 'pointer',
            fontFamily: 'inherit',
            outline: 'none',
          }}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <ChevronRight
          size={14}
          color={theme.textTertiary}
          strokeWidth={1.75}
          style={{
            position: 'absolute',
            insetInlineEnd: 8,
            top: '50%',
            transform: 'translateY(-50%) rotate(90deg)',
            pointerEvents: 'none',
          }}
        />
      </div>
    </div>
  );
}

export function PreferencesPanel() {
  const { theme, themeMode, setThemeMode } = useTheme();
  const { t } = useTranslation('common');
  const { locale, changeLocale } = useLocale();
  const iconColor = theme.textSecondary;

  const themeOptions = [
    { value: 'system' as const, label: t('settings.themeSystem') },
    { value: 'light' as const, label: t('settings.themeLight') },
    { value: 'dark' as const, label: t('settings.themeDark') },
  ];

  const localeOptions = LOCALES.map((value) => ({
    value,
    label: LOCALE_LABELS[value],
  }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <PanelHeader title={t('settings.categories.preferences')} />
      <div style={{ borderRadius: 12, background: theme.surface, overflow: 'hidden' }}>
        <SettingsSelectRow
          icon={<SunMoon size={ICON_SIZE} color={iconColor} strokeWidth={ICON_STROKE} />}
          label={t('settings.theme')}
          value={themeMode}
          options={themeOptions}
          onChange={(next) => setThemeMode(next)}
        />
        <SettingsSelectRow
          icon={<Globe size={ICON_SIZE} color={iconColor} strokeWidth={ICON_STROKE} />}
          label={t('settings.language')}
          value={locale}
          options={localeOptions}
          onChange={(next) => changeLocale(next)}
          isLast
        />
      </div>
      <JobPreferenceLinks />
    </div>
  );
}

export { PrivacyPanel } from './panels/PrivacyPanel';

const NOTIFICATION_PREFERENCE_ROWS: Array<{
  key: keyof NotificationPreferences;
  icon: React.ComponentType<{ size?: number; color?: string; strokeWidth?: number }>;
  labelKey: string;
  descriptionKey: string;
}> = [
  {
    key: 'marketingEmailsEnabled',
    icon: Megaphone,
    labelKey: 'settings.notificationPreferences.marketingEmails',
    descriptionKey: 'settings.notificationPreferences.marketingEmailsDescription',
  },
  {
    key: 'systemEmailsEnabled',
    icon: Mail,
    labelKey: 'settings.notificationPreferences.systemEmails',
    descriptionKey: 'settings.notificationPreferences.systemEmailsDescription',
  },
  {
    key: 'pushNotificationsEnabled',
    icon: Bell,
    labelKey: 'settings.notificationPreferences.pushNotifications',
    descriptionKey: 'settings.notificationPreferences.pushNotificationsDescription',
  },
];


function PreferenceSwitch({
  checked,
  disabled,
  accessibilityLabel,
  onToggle,
}: {
  checked: boolean;
  disabled: boolean;
  accessibilityLabel?: string;
  onToggle: () => void;
}) {
  const { theme } = useTheme();
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={accessibilityLabel}
      disabled={disabled}
      onClick={onToggle}
      style={{
        width: 44,
        height: 26,
        borderRadius: 999,
        border: 'none',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        position: 'relative',
        padding: 0,
        flexShrink: 0,
        backgroundColor: checked ? colors.primary : theme.surfaceTertiary,
        transition: 'background-color 150ms ease, opacity 150ms ease',
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 2,
          insetInlineStart: checked ? 20 : 2,
          width: 22,
          height: 22,
          borderRadius: 11,
          backgroundColor: theme.background,
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.2)',
          transition: 'inset-inline-start 150ms ease',
        }}
      />
    </button>
  );
}

export function NotificationsPanel() {
  const { theme } = useTheme();
  const { t } = useTranslation('common');
  const toast = useToast();
  const {
    preferences,
    isLoading,
    isSaving,
    error,
    updatePreferences,
  } = useNotificationPreferences();
  const iconColor = theme.textSecondary;
  const disabled = isLoading || isSaving || Boolean(error);

  const handleToggle = (
    key: keyof NotificationPreferences,
    value: boolean,
  ) => {
    const patch = { [key]: value } as NotificationPreferencesPatch;
    void updatePreferences(patch).catch((err) => {
      toast.error(
        t('settings.notificationPreferences.updateFailed'),
        err instanceof Error ? err.message : undefined,
      );
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <PanelHeader title={t('settings.categories.notifications')} />
      <div style={{ borderRadius: 12, background: theme.surface, overflow: 'hidden' }}>
        {NOTIFICATION_PREFERENCE_ROWS.map((row, index) => {
          const Icon = row.icon;
          const value = preferences[row.key];
          return (
            <SettingsRow
              key={row.key}
              icon={
                <Icon
                  size={ICON_SIZE}
                  color={iconColor}
                  strokeWidth={ICON_STROKE}
                />
              }
              label={t(row.labelKey)}
              description={t(row.descriptionKey)}
              rightElement={
                isLoading ? (
                  <Loader2
                    size={16}
                    color={theme.textTertiary}
                    strokeWidth={ICON_STROKE}
                  />
                ) : (
                  <PreferenceSwitch
                    checked={value}
                    disabled={disabled}
                    onToggle={() => handleToggle(row.key, !value)}
                  />
                )
              }
              isLast={index === NOTIFICATION_PREFERENCE_ROWS.length - 1}
              value={isLoading ? t('actions.loading') : undefined}
            />
          );
        })}
      </div>
      <div
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
        }}
        aria-live="polite"
      >
        {isSaving ? t('settings.notificationPreferences.saving') : ''}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MCP access panel
// ---------------------------------------------------------------------------

function formatMcpDate(value?: string | Date | null) {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleDateString();
}

function copyTextToClipboard(value: string) {
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(value);
  }

  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
  return Promise.resolve();
}

function McpApprovalRow({
  approval,
  onResolved,
}: {
  approval: McpPendingApproval;
  onResolved: () => void;
}) {
  const { theme } = useTheme();
  const { t } = useTranslation('common');
  const toast = useToast();
  const [busy, setBusy] = useState<'approve' | 'reject' | null>(null);

  const handleApprove = async () => {
    setBusy('approve');
    try {
      await confirmApproval(approval.id);
      toast.success(t('settings.mcpAccess.approved'));
      onResolved();
    } catch (error) {
      // B10: a stale or expired approval was closed by the server; say why
      // in the user's language and drop it from the list.
      const copyKey = approvalFailureCopyKey(error);
      toast.error(
        t('settings.mcpAccess.approvalFailed'),
        copyKey ? t(copyKey) : error instanceof Error ? error.message : undefined,
      );
      if (copyKey) onResolved();
    } finally {
      setBusy(null);
    }
  };

  const handleReject = async () => {
    setBusy('reject');
    try {
      await rejectApproval(approval.id);
      toast.success(t('settings.mcpAccess.rejected'));
      onResolved();
    } catch (error) {
      toast.error(
        t('settings.mcpAccess.approvalFailed'),
        error instanceof Error ? error.message : undefined,
      );
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        border: `1px solid ${theme.borderSubtle}`,
        borderRadius: 10,
        padding: '10px 12px',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: theme.text,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {approval.toolName}
        </div>
        <div style={{ marginTop: 2, fontSize: 12, color: theme.textTertiary }}>
          {formatMcpDate(approval.createdAt)}
        </div>
      </div>
      <button
        type="button"
        aria-label={t('settings.mcpAccess.reject')}
        onClick={() => {
          void handleReject();
        }}
        disabled={Boolean(busy)}
        style={{
          width: 34,
          height: 34,
          borderRadius: 17,
          border: `1px solid ${theme.borderSubtle}`,
          background: 'transparent',
          color: '#EF4444',
          cursor: busy ? 'default' : 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: busy ? 0.6 : 1,
        }}
      >
        <XCircle size={18} />
      </button>
      <button
        type="button"
        aria-label={t('settings.mcpAccess.approve')}
        onClick={() => {
          void handleApprove();
        }}
        disabled={Boolean(busy)}
        style={{
          width: 34,
          height: 34,
          borderRadius: 17,
          border: `1px solid ${theme.borderSubtle}`,
          background: 'transparent',
          color: '#5e29ff',
          cursor: busy ? 'default' : 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: busy ? 0.6 : 1,
        }}
      >
        {busy === 'approve' ? (
          <Loader2 size={17} className="animate-spin" />
        ) : (
          <CheckCircle size={18} />
        )}
      </button>
    </div>
  );
}

export function McpAccessPanel() {
  const { theme } = useTheme();
  const { t } = useTranslation('common');
  const toast = useToast();
  const [state, setState] = useState<McpAccessState | null>(null);
  const [includePersonal, setIncludePersonal] = useState(true);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>([]);
  const [secret, setSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<'create' | 'rotate' | 'revoke' | null>(null);

  const loadState = useCallback(async () => {
    setLoading(true);
    try {
      const next = await fetchMcpAccessState();
      setState(next);
      const token = next.tokens[0];
      if (token) {
        setIncludePersonal(token.includePersonal ?? token.scopeType !== 'company');
        setSelectedCompanyIds(
          token.companyIds?.length
            ? token.companyIds
            : token.companyId
              ? [token.companyId]
              : [],
        );
      }
    } catch (error) {
      toast.error(
        t('settings.mcpAccess.loadFailed'),
        error instanceof Error ? error.message : undefined,
      );
    } finally {
      setLoading(false);
    }
  }, [t, toast]);

  useEffect(() => {
    void loadState();
  }, [loadState]);

  const companyOptions = (state?.scopes ?? []).filter(
    (scope) => scope.scopeType === 'company',
  );
  const activeToken = state?.tokens[0];
  const canCreate = includePersonal || selectedCompanyIds.length > 0;
  const selectedCompanyNames = companyOptions
    .filter((scope) => selectedCompanyIds.includes(scope.companyId ?? ''))
    .map((scope) => scope.label);
  const accessSummary = [
    includePersonal ? t('settings.mcpAccess.personalScope') : null,
    ...selectedCompanyNames,
  ]
    .filter(Boolean)
    .join(' + ');

  const copyValue = async (value: string, message = t('settings.mcpAccess.copied')) => {
    try {
      await copyTextToClipboard(value);
      toast.success(message);
    } catch (error) {
      toast.error(
        t('settings.mcpAccess.copyFailed'),
        error instanceof Error ? error.message : undefined,
      );
    }
  };

  const handleCreate = async () => {
    if (!canCreate) return;

    setBusy('create');
    try {
      const result = await createMcpToken({
        includePersonal,
        companyIds: selectedCompanyIds,
      });
      setSecret(result.secret);
      toast.success(t('settings.mcpAccess.created'));
      await loadState();
    } catch (error) {
      toast.error(
        t('settings.mcpAccess.createFailed'),
        error instanceof Error ? error.message : undefined,
      );
    } finally {
      setBusy(null);
    }
  };

  const toggleCompany = (companyId: string | null) => {
    if (!companyId) return;
    setSelectedCompanyIds((current) =>
      current.includes(companyId)
        ? current.filter((id) => id !== companyId)
        : [...current, companyId],
    );
  };

  const handleRotate = async () => {
    if (!activeToken || !canCreate) return;
    const ok = await confirm({
      title: t('settings.mcpAccess.rotateTitle'),
      message: t('settings.mcpAccess.rotateMessage'),
      confirmLabel: t('settings.mcpAccess.rotate'),
      cancelLabel: t('actions.cancel'),
    });
    if (!ok) return;

    setBusy('rotate');
    try {
      const result = await createMcpToken({
        includePersonal,
        companyIds: selectedCompanyIds,
      });
      setSecret(result.secret);
      toast.success(t('settings.mcpAccess.rotated'));
      await loadState();
    } catch (error) {
      toast.error(
        t('settings.mcpAccess.rotateFailed'),
        error instanceof Error ? error.message : undefined,
      );
    } finally {
      setBusy(null);
    }
  };

  const handleRevoke = async () => {
    if (!activeToken) return;
    const ok = await confirm({
      title: t('settings.mcpAccess.revokeTitle'),
      message: t('settings.mcpAccess.revokeMessage'),
      confirmLabel: t('settings.mcpAccess.revoke'),
      cancelLabel: t('actions.cancel'),
      destructive: true,
    });
    if (!ok) return;

    setBusy('revoke');
    try {
      await revokeMcpToken(activeToken.id);
      setSecret(null);
      toast.success(t('settings.mcpAccess.revoked'));
      await loadState();
    } catch (error) {
      toast.error(
        t('settings.mcpAccess.revokeFailed'),
        error instanceof Error ? error.message : undefined,
      );
    } finally {
      setBusy(null);
    }
  };

  if (loading && !state) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <PanelHeader
          title={t('settings.mcpAccess.title')}
          subtitle={t('settings.mcpAccess.scopeHelp')}
        />
        <div style={{ padding: 32, display: 'flex', justifyContent: 'center' }}>
          <Loader2
            size={18}
            className="animate-spin"
            style={{ color: theme.textTertiary }}
          />
        </div>
      </div>
    );
  }

  const statusDot = activeToken ? '#10B981' : theme.textTertiary;
  const statusLabel = activeToken
    ? t('settings.mcpAccess.enabled')
    : t('settings.mcpAccess.notCreated');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <PanelHeader
        title={t('settings.mcpAccess.title')}
        subtitle={t('settings.mcpAccess.scopeHelp')}
      />

      {/* Status row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          fontSize: 13,
          color: theme.textSecondary,
        }}
      >
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: 4,
            background: statusDot,
            flexShrink: 0,
          }}
        />
        {statusLabel}
      </div>

      {/* Server URL */}
      {state?.serverUrl ? (
        <SettingsField label={t('settings.mcpAccess.serverUrl')}>
          <button
            type="button"
            onClick={() => {
              void copyValue(state.serverUrl, t('settings.mcpAccess.urlCopied'));
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              width: '100%',
              padding: '10px 12px',
              borderRadius: 10,
              border: `1px solid ${theme.borderSubtle}`,
              background: theme.surface,
              cursor: 'pointer',
              fontFamily: 'inherit',
              transition: 'background 120ms ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--alpha-04, rgba(0,0,0,0.03))';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = theme.surface;
            }}
          >
            <span
              style={{
                flex: 1,
                minWidth: 0,
                fontSize: 13,
                fontFamily:
                  'ui-monospace, SFMono-Regular, Menlo, Monaco, monospace',
                color: theme.text,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textAlign: 'start',
              }}
            >
              {state.serverUrl}
            </span>
            <Copy size={14} color={theme.textTertiary} />
          </button>
        </SettingsField>
      ) : null}

      {/* Token (active) */}
      {activeToken ? (
        <SettingsField
          label={t('settings.mcpAccess.token')}
          hint={
            activeToken.lastUsedAt
              ? t('settings.mcpAccess.lastUsed', {
                  date: formatMcpDate(activeToken.lastUsedAt),
                })
              : t('settings.mcpAccess.neverUsed')
          }
        >
          <div
            style={{
              padding: '10px 12px',
              borderRadius: 10,
              border: `1px solid ${theme.borderSubtle}`,
              background: theme.surface,
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
            }}
          >
            <span
              style={{
                fontSize: 13,
                fontFamily:
                  'ui-monospace, SFMono-Regular, Menlo, Monaco, monospace',
                color: theme.text,
                wordBreak: 'break-all',
              }}
            >
              {activeToken.maskedToken}
            </span>
            <span style={{ fontSize: 12, color: theme.textTertiary }}>
              {[
                activeToken.includePersonal
                  ? t('settings.mcpAccess.personalScope')
                  : null,
                ...(activeToken.companies ?? []).map((company) => company.name),
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </div>
        </SettingsField>
      ) : null}

      {/* One-time secret */}
      {secret ? (
        <button
          type="button"
          onClick={() => {
            void copyValue(secret, t('settings.mcpAccess.secretCopied'));
          }}
          style={{
            width: '100%',
            border: '1px solid #5e29ff',
            borderRadius: 12,
            background: 'rgba(94, 41, 255, 0.04)',
            padding: 14,
            textAlign: 'start',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 13,
              fontWeight: 600,
              color: '#5e29ff',
            }}
          >
            <span style={{ flex: 1 }}>{t('settings.mcpAccess.oneTimeToken')}</span>
            <Copy size={14} />
          </div>
          <div
            style={{
              fontSize: 13,
              fontFamily:
                'ui-monospace, SFMono-Regular, Menlo, Monaco, monospace',
              color: theme.text,
              wordBreak: 'break-all',
            }}
          >
            {secret}
          </div>
          <div style={{ fontSize: 12, lineHeight: 1.45, color: theme.textTertiary }}>
            {t('settings.mcpAccess.oneTimeWarning')}
          </div>
        </button>
      ) : null}

      {/* Scope */}
      <SettingsField label={t('settings.mcpAccess.scope')}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 12,
            overflow: 'hidden',
            background: theme.surface,
          }}
        >
          <ScopeRow
            icon={<UserRound size={16} color={theme.textSecondary} strokeWidth={1.75} />}
            title={t('settings.mcpAccess.personalScope')}
            description={t('settings.mcpAccess.personalScopeDescription')}
            checked={includePersonal}
            onToggle={() => setIncludePersonal((value) => !value)}
            isLast={!companyOptions.length}
          />
          {companyOptions.map((scope, idx) => {
            const selected = selectedCompanyIds.includes(scope.companyId ?? '');
            return (
              <ScopeRow
                key={scope.companyId}
                icon={
                  <Building2 size={16} color={theme.textSecondary} strokeWidth={1.75} />
                }
                title={scope.label}
                description={t('settings.mcpAccess.companyScopeDescription')}
                checked={selected}
                onToggle={() => toggleCompany(scope.companyId)}
                isLast={idx === companyOptions.length - 1}
              />
            );
          })}
        </div>
        <div
          style={{
            marginTop: 8,
            fontSize: 12,
            color: canCreate ? theme.textTertiary : '#EF4444',
          }}
        >
          {canCreate ? accessSummary : t('settings.mcpAccess.selectScopeRequired')}
        </div>
      </SettingsField>

      {/* Actions */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <button
          type="button"
          onClick={() => {
            void (activeToken ? handleRotate() : handleCreate());
          }}
          disabled={Boolean(busy) || !canCreate}
          style={{
            height: 36,
            padding: '0 18px',
            borderRadius: 999,
            border: 'none',
            background: '#5e29ff',
            color: '#fff',
            fontSize: 13,
            fontWeight: 600,
            cursor: busy || !canCreate ? 'default' : 'pointer',
            opacity: busy || !canCreate ? 0.6 : 1,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          {busy === 'create' || busy === 'rotate' ? (
            <Loader2 size={14} className="animate-spin" />
          ) : null}
          {activeToken
            ? t('settings.mcpAccess.rotate')
            : t('settings.mcpAccess.create')}
        </button>
        {activeToken ? (
          <button
            type="button"
            onClick={() => {
              void handleRevoke();
            }}
            disabled={Boolean(busy)}
            style={{
              height: 36,
              padding: '0 14px',
              borderRadius: 999,
              border: 'none',
              background: 'transparent',
              color: '#EF4444',
              fontSize: 13,
              fontWeight: 600,
              cursor: busy ? 'default' : 'pointer',
              opacity: busy ? 0.6 : 1,
            }}
          >
            {t('settings.mcpAccess.revoke')}
          </button>
        ) : null}
      </div>

      {/* Pending approvals */}
      {state?.pendingApprovals.length ? (
        <SettingsField label={t('settings.mcpAccess.pendingApprovals')}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {state.pendingApprovals.map((approval) => (
              <McpApprovalRow
                key={approval.id}
                approval={approval}
                onResolved={() => {
                  void loadState();
                }}
              />
            ))}
          </div>
        </SettingsField>
      ) : null}
    </div>
  );
}

function SettingsField({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  const { theme } = useTheme();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <label
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: theme.textSecondary,
          letterSpacing: 0.2,
        }}
      >
        {label}
      </label>
      {children}
      {hint ? (
        <span style={{ fontSize: 12, color: theme.textTertiary }}>{hint}</span>
      ) : null}
    </div>
  );
}

function ScopeRow({
  icon,
  title,
  description,
  checked,
  onToggle,
  isLast,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  checked: boolean;
  onToggle: () => void;
  isLast: boolean;
}) {
  const { theme } = useTheme();
  return (
    <button
      type="button"
      onClick={onToggle}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        padding: '12px 14px',
        background: 'transparent',
        border: 'none',
        borderBottom: isLast ? 'none' : `0.5px solid ${theme.borderSubtle}`,
        cursor: 'pointer',
        textAlign: 'start',
        transition: 'background 120ms ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'var(--alpha-04, rgba(0,0,0,0.03))';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
      }}
    >
      <span
        style={{
          width: 28,
          height: 28,
          borderRadius: 8,
          background: 'var(--alpha-05, rgba(0,0,0,0.04))',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span
          style={{
            display: 'block',
            fontSize: 14,
            fontWeight: 500,
            color: theme.text,
          }}
        >
          {title}
        </span>
        <span
          style={{
            display: 'block',
            marginTop: 1,
            fontSize: 12,
            color: theme.textTertiary,
          }}
        >
          {description}
        </span>
      </span>
      <Checkbox checked={checked} />
    </button>
  );
}

function Checkbox({ checked }: { checked: boolean }) {
  const { theme } = useTheme();
  return (
    <span
      style={{
        width: 18,
        height: 18,
        borderRadius: 5,
        border: `1.5px solid ${checked ? '#5e29ff' : theme.borderSubtle}`,
        background: checked ? '#5e29ff' : 'transparent',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        transition: 'background 120ms ease, border-color 120ms ease',
      }}
    >
      {checked ? <Check size={11} color="#fff" strokeWidth={2} /> : null}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Agent Access panel (Skills Hub permissions + run log)
// ---------------------------------------------------------------------------

export { AgentAccessPanel } from './panels/AgentAccessPanel';

// ---------------------------------------------------------------------------
// AI Memory panel
// ---------------------------------------------------------------------------

type MemoryPanelMode = 'idle' | 'viewing' | 'editing';
type MemoryPanelStatus = { message: string; tone: 'success' | 'error' };

function countWorkingMemoryFields(memory: WorkingMemoryV1 | null) {
  if (!memory) return 0;
  return Object.entries(memory).reduce((count, [, value]) => {
    if (value === null || value === undefined) return count;
    if (typeof value === 'string') return value.trim() ? count + 1 : count;
    if (Array.isArray(value)) return value.length ? count + 1 : count;
    if (typeof value === 'object')
      return Object.keys(value).length ? count + 1 : count;
    return count + 1;
  }, 0);
}

function formatMemoryDraft(memory: WorkingMemoryV1 | null) {
  return JSON.stringify(memory ?? {}, null, 2);
}

export function MemoryPanel() {
  const { theme } = useTheme();
  const { t } = useTranslation('common');
  const [mode, setMode] = useState<MemoryPanelMode>('idle');
  const [memory, setMemory] = useState<WorkingMemoryV1 | null>(null);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<MemoryPanelStatus | null>(null);

  const loadMemory = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchMemory();
      setMemory(data);
      setDraft(formatMemoryDraft(data));
    } catch {
      setMemory(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleView = () => {
    if (memory === null) loadMemory();
    setMode('viewing');
    setStatus(null);
  };

  const handleEdit = () => {
    if (memory === null) loadMemory();
    setMode('editing');
    setStatus(null);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const parsed = parseWorkingMemoryDraft(draft);
      const next = await updateMemory(parsed);
      setMemory(next);
      setDraft(formatMemoryDraft(next));
      setMode('viewing');
      setStatus({ message: t('settings.memorySaved'), tone: 'success' });
    } catch (error) {
      setStatus({
        message: t(getWorkingMemorySaveErrorKey(error)),
        tone: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClear = async () => {
    const ok = await confirm({
      message: t('settings.clearMemoryConfirm'),
      confirmLabel: t('actions.delete'),
      cancelLabel: t('actions.cancel'),
      destructive: true,
    });
    if (!ok) return;
    setLoading(true);
    try {
      const next = await clearMemory();
      setMemory(next);
      setDraft(formatMemoryDraft(next));
      setMode('idle');
      setStatus({ message: t('settings.memoryCleared'), tone: 'success' });
    } catch {
      setStatus({
        message: t('settings.memoryGraph.advancedClearError'),
        tone: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadMemory();
  }, [loadMemory]);

  const factCount = countWorkingMemoryFields(memory);
  const iconColor = theme.textSecondary;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <PanelHeader title={t('settings.aiMemory')} subtitle={t('settings.memoryGraph.listSubtitle')} />

      {/* M5.2: durable memory, grouped list surface (web-only). */}
      <MemoryList />

      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          letterSpacing: 0.3,
          textTransform: 'uppercase',
          color: theme.textTertiary,
          padding: '4px 4px 0',
        }}
      >
        {t('settings.memoryGraph.advancedTitle')}
      </div>
      <div style={{ borderRadius: 12, background: theme.surface, overflow: 'hidden' }}>
        <SettingsRow
          icon={<Eye size={ICON_SIZE} color={iconColor} strokeWidth={ICON_STROKE} />}
          label={t('settings.viewMemory')}
          value={loading ? '...' : factCount ? `${factCount} ${t('memoryFields')}` : ''}
          onPress={handleView}
          showChevron
        />
        <SettingsRow
          icon={<Pencil size={ICON_SIZE} color={iconColor} strokeWidth={ICON_STROKE} />}
          label={t('settings.editMemory')}
          onPress={handleEdit}
          showChevron
        />
        <SettingsRow
          icon={<Trash2 size={ICON_SIZE} color="#EF4444" strokeWidth={ICON_STROKE} />}
          label={t('settings.clearMemory')}
          onPress={handleClear}
          destructive
          isLast
        />
      </div>

      {mode !== 'idle' ? (
        <div
          style={{
            padding: 14,
            borderRadius: 12,
            background: theme.surface,
          }}
        >
          {loading && mode !== 'editing' ? (
            <div className="flex items-center justify-center py-4">
              <Loader2
                size={18}
                className="animate-spin"
                style={{ color: theme.textTertiary }}
              />
            </div>
          ) : !factCount && mode === 'viewing' ? (
            <p style={{ fontSize: 13, color: theme.textTertiary, margin: 0 }}>
              {t('settings.noMemory')}
            </p>
          ) : mode === 'viewing' ? (
            <pre
              style={{
                fontSize: 13,
                lineHeight: 1.6,
                color: theme.text,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                margin: 0,
                maxHeight: 280,
                overflowY: 'auto',
                fontFamily: 'inherit',
              }}
            >
              {formatMemoryDraft(memory)}
            </pre>
          ) : (
            <div className="flex flex-col gap-2">
              <textarea
                value={draft}
                onChange={(e) => {
                  setDraft(e.target.value);
                  setStatus(null);
                }}
                rows={10}
                style={{
                  width: '100%',
                  fontSize: 13,
                  lineHeight: 1.6,
                  color: theme.text,
                  background: theme.background,
                  border: `1px solid ${theme.borderSubtle}`,
                  borderRadius: 10,
                  padding: '10px 12px',
                  resize: 'vertical',
                  outline: 'none',
                  fontFamily: 'inherit',
                }}
              />
              <div className="flex items-center gap-2 justify-end">
                <button
                  onClick={() => {
                    setMode('viewing');
                    setDraft(formatMemoryDraft(memory));
                    setStatus(null);
                  }}
                  style={{
                    padding: '6px 16px',
                    fontSize: 13,
                    fontWeight: 500,
                    borderRadius: 8,
                    border: `1px solid ${theme.buttonOutlineBorder}`,
                    background: 'transparent',
                    color: theme.text,
                    cursor: 'pointer',
                  }}
                >
                  {t('actions.cancel')}
                </button>
                <button
                  onClick={() => {
                    void handleSave();
                  }}
                  disabled={loading}
                  style={{
                    padding: '6px 16px',
                    fontSize: 13,
                    fontWeight: 600,
                    borderRadius: 8,
                    border: 'none',
                    background: '#5e29ff',
                    color: '#fff',
                    cursor: loading ? 'default' : 'pointer',
                    opacity: loading ? 0.6 : 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  {loading ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Save size={14} />
                  )}
                  {t('actions.save')}
                </button>
              </div>
              <p style={{ fontSize: 12, color: theme.textTertiary, margin: 0 }}>
                {t('memoryEditHelp')}
              </p>
            </div>
          )}
          {status ? (
            <p
              role={status.tone === 'error' ? 'alert' : 'status'}
              style={{
                fontSize: 12,
                color: status.tone === 'error' ? theme.danger : theme.textTertiary,
                marginTop: 8,
                marginBottom: 0,
              }}
            >
              {status.message}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// About
// ---------------------------------------------------------------------------

export function AboutPanel({
  onOpenFeedback,
}: {
  /**
   * Inside the settings dialog Feedback is a sibling category, so the shell
   * switches panels; the `/settings/about` page has no shell and navigates.
   */
  onOpenFeedback?: () => void;
} = {}) {
  const { theme } = useTheme();
  const { t } = useTranslation('common');
  const { t: tTour } = useTranslation('tour');
  const { locale } = useLocale();
  const { activeWorkspace } = useWorkspace();
  const router = useRouter();
  const dialog = useDialog();
  const iconColor = theme.textSecondary;

  // The tour walks the personal-workspace sidebar; close the settings dialog
  // first so its backdrop doesn't sit on top of the spotlight overlay.
  const handleReplayTour = () => {
    dialog.close();
    // An on-demand replay must end on chat where it started, so drop any
    // destination an earlier (abandoned) post-onboarding redirect parked -
    // otherwise finish() would fling the user off to it.
    clearPostTourDestination();
    router.replace(TOUR_SURFACE_HREF as never);
    requestTourStart();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <PanelHeader title={t('settings.about')} />
      <div style={{ borderRadius: 12, background: theme.surface, overflow: 'hidden' }}>
        {activeWorkspace.type === 'personal' ? (
          <SettingsRow
            icon={
              <Sparkles size={ICON_SIZE} color={iconColor} strokeWidth={ICON_STROKE} />
            }
            label={tTour('settings.replayLabel')}
            description={tTour('settings.replayDescription')}
            showChevron
            onPress={handleReplayTour}
          />
        ) : null}
        <SettingsRow
          icon={
            <Mail size={ICON_SIZE} color={iconColor} strokeWidth={ICON_STROKE} />
          }
          label={t('feedback.floatingLabel')}
          showChevron
          onPress={() => {
            if (onOpenFeedback) {
              onOpenFeedback();
              return;
            }
            dialog.close();
            router.push('/settings/feedback');
          }}
        />
        <SettingsRow
          icon={<FileText size={ICON_SIZE} color={iconColor} strokeWidth={ICON_STROKE} />}
          label={t('settings.termsOfUse')}
          showChevron
          onPress={() => {
            void openMarketingLink('terms', locale);
          }}
        />
        <SettingsRow
          icon={<Lock size={ICON_SIZE} color={iconColor} strokeWidth={ICON_STROKE} />}
          label={t('settings.privacyPolicy')}
          showChevron
          onPress={() => {
            void openMarketingLink('privacy', locale);
          }}
        />
        <SettingsRow
          icon={
            <span
              className="rounded-full inline-block"
              style={{ width: 14, height: 14, background: theme.text }}
            />
          }
          label={t('tabbioForWeb')}
          value={APP_VERSION}
          isLast
        />
      </div>
    </div>
  );
}

export { FeedbackPanel } from './panels/FeedbackPanel';

export { SettingsRouteIndex as SettingsRouteWebScreen } from './SettingsRouteIndex.web';
